type CardSize = {
  d: string; // desktop tailwind size classes (md:w-[..] md:h-[..])
  m: string; // mobile tailwind size classes (w-full + min-h-[..])
};

const BORDER = "#97A7A666";

function CardShell({
  sizes,
  imgD,
  imgM,
  children,
}: {
  sizes: CardSize;
  imgD: string;
  imgM: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border md:rounded-[20px] ${sizes.m} ${sizes.d}`}
      style={{ borderColor: BORDER }}
    >
      <img
        src={imgD}
        alt=""
        className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover object-right transition-[filter] duration-300 md:block md:grayscale md:group-hover:grayscale-0"
        loading="lazy"
        decoding="async"
      />
      <img
        src={imgM}
        alt=""
        className="pointer-events-none absolute inset-0 block h-full w-full object-cover object-right md:hidden"
        loading="lazy"
        decoding="async"
      />
      <div className="relative z-10 flex h-full flex-col p-4 md:p-5">
        {children}
      </div>
    </div>
  );
}

function StoryContent({
  label,
  title,
  description,
}: {
  label: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex max-w-[70%] md:max-w-[75%] flex-col gap-2 md:gap-1">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/70 md:text-sm">
        {label}
      </p>
      <h3 className="text-base font-semibold leading-snug text-white md:text-lg">
        {title}
      </h3>
      <p className="text-[13px] leading-snug text-white/55 md:text-sm">
        {description}
      </p>
    </div>
  );
}

function StatContent({
  numberMain,
  numberSuffix,
  caption,
}: {
  numberMain: string;
  numberSuffix?: string;
  caption: string;
}) {
  return (
    <div className="flex max-w-[65%] flex-col gap-2 md:gap-2.5">
      <p className="whitespace-nowrap font-fira-condensed text-[36px] font-semibold leading-none tracking-tight text-white md:text-[44px]">
        {numberMain}
        {numberSuffix ? (
          <span className="ml-0.5 text-base font-medium md:text-lg">
            {numberSuffix}
          </span>
        ) : null}
      </p>
      <p className="text-[13px] leading-snug text-white/55 md:text-sm">
        {caption}
      </p>
    </div>
  );
}

export default function BusinessLayers() {
  return (
    <section
      id="business-layers"
      className="w-full font-fira text-white py-12 md:py-20"
      style={{ backgroundColor: "#10100F" }}
    >
      <div className="mx-auto w-[90%] md:w-[80%]">
        <p className="font-fira text-sm font-normal uppercase tracking-[0.18em] text-[#FFFFFF]">
          What teams get
        </p>
        <h2 className="mt-3 font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:mt-4 md:text-[42px] md:font-semibold">
          Live sessions. Daily reinforcement.
          <br className="hidden md:block" />
          {" "}Real time saved.
        </h2>
        <p className="mt-3 font-fira text-sm font-normal leading-relaxed text-[#FFFFFF85] md:mt-4 md:text-base">
          Five layers covering how teams actually build AI capability - and the
          outcomes that follow.
        </p>

        <div className="mt-8 flex flex-col gap-4 md:mt-10">
          {/* Row 1: 3 cards */}
          <div className="flex flex-col gap-4 md:flex-row md:gap-5">
            <CardShell
              imgD="/business/SD1.webp"
              imgM="/business/SM1.webp"
              sizes={{
                d: "md:flex-[635] md:min-w-[400px] md:h-[167px] ",
                m: "w-full h-[248px]",
              }}
            >
              <StoryContent
                label="Live · High-touch"
                title="AI Mastermind for Generalists/ Engineers"
                description="Live, structured sessions built around your team's real workflows. Teams work through problems together and leave with things they can apply immediately. Not a lecture."
              />
            </CardShell>

            <CardShell
              imgD="/business/SD2.webp"
              imgM="/business/SM2.webp"
              sizes={{
                d: "md:flex-[240] md:min-w-[200px] md:h-[167px]",
                m: "w-full h-[127px]",
              }}
            >
              <StatContent
                numberMain="30–50%"
                caption="Time saved on repetitive tasks within month one"
              />
            </CardShell>

            <CardShell
              imgD="/business/SD3.webp"
              imgM="/business/SM3.webp"
              sizes={{
                d: "md:flex-[240] md:min-w-[200px] md:h-[167px]",
                m: "w-full h-[127px]",
              }}
            >
              <StatContent
                numberMain="40+"
                numberSuffix="hrs"
                caption="Reclaimed weekly across a team of 20"
              />
            </CardShell>
          </div>

          {/* Row 2: 4 cards */}
          <div className="flex flex-col gap-4 md:flex-row md:gap-5">
            <CardShell
              imgD="/business/SD4.webp"
              imgM="/business/SM4.webp"
              sizes={{
                d: "md:flex-[248] md:min-w-[200px] md:h-[167px]",
                m: "w-full h-[167px]",
              }}
            >
              <StoryContent
                label="On-demand"
                title="Recordings"
                description="Every session recorded. New joiners onboard at their own pace."
              />
            </CardShell>

            <CardShell
              imgD="/business/SD5.webp"
              imgM="/business/SM5.webp"
              sizes={{
                d: "md:flex-[252] md:min-w-[200px] md:h-[167px]",
                m: "w-full h-[167px]",
              }}
            >
              <StoryContent
                label="Org-specific"
                title="Branded Content"
                description="Guides and prompt libraries adapted to your team's tools."
              />
            </CardShell>

            <CardShell
              imgD="/business/SD6.webp"
              imgM="/business/SM6.webp"
              sizes={{
                d: "md:flex-[298] md:min-w-[220px] md:h-[167px]",
                m: "w-full h-[164px]",
              }}
            >
              <StoryContent
                label="Daily"
                title="WhatsApp"
                description="A short tutorial video and PDF in your team's WhatsApp every day."
              />
            </CardShell>

            <CardShell
              imgD="/business/SD7.webp"
              imgM="/business/SM7.webp"
              sizes={{
                d: "md:flex-[240] md:min-w-[200px] md:h-[167px]",
                m: "w-full h-[167px]",
              }}
            >
              <StoryContent
                label="Weekly"
                title="Newsletter"
                description="Focused weekly read on what changed in AI — for decision-makers."
              />
            </CardShell>
          </div>
        </div>
      </div>
    </section>
  );
}
